/**
 * Curated municipal bond attribute taxonomy for Advanced Search → Attributes.
 * Stable codes are the API contract; labels are display-only.
 *
 * Display order is grouped under Credit Enhancement, Regulatory / Offering Type,
 * Structure / Cash Flow, ESG / Labelled Bonds, and Other (unclassified leftovers).
 *
 * Tax-treatment codes (Fed/State/AMT/Bank Qualified) live in the dedicated
 * Tax filter (`taxAttributes.ts`), not here.
 */

export type BondAttributeCode =
  | "pre_refunded"
  | "escrowed"
  | "insured"
  | "enhanced"
  | "prepaid_gas"
  | "gic_liquidity_provider"
  | "putable"
  | "sinking_fund"
  | "convertible"
  | "rule_144a_qib"
  | "green"
  | "sri"
  | "factored";

export interface BondAttributeOption {
  code: BondAttributeCode;
  label: string;
}

export interface BondAttributeGroup {
  /** Section header title (rendered uppercase). */
  title: string;
  options: readonly BondAttributeOption[];
}

/**
 * Grouped display order for the Advanced Search Attributes accordion.
 * Codes not listed under a named category land in Other.
 */
export const BOND_ATTRIBUTE_GROUPS: readonly BondAttributeGroup[] = [
  {
    title: "Credit Enhancement",
    options: [
      { code: "insured", label: "Insured" },
      { code: "enhanced", label: "Enhancement / Collateral" },
      { code: "gic_liquidity_provider", label: "GIC/Liquidity Provider" },
      { code: "prepaid_gas", label: "Prepaid Gas" },
    ],
  },
  {
    title: "Regulatory / Offering Type",
    options: [
      { code: "rule_144a_qib", label: "144A / QIB" },
    ],
  },
  {
    title: "Structure / Cash Flow",
    options: [
      { code: "putable", label: "Putable" },
      { code: "sinking_fund", label: "Sinking Fund" },
      { code: "convertible", label: "Convertible" },
      { code: "factored", label: "Factored Bonds" },
    ],
  },
  {
    title: "ESG / Labelled Bonds",
    options: [
      { code: "green", label: "Green Bonds" },
      { code: "sri", label: "SRI Bonds" },
    ],
  },
  {
    title: "Other",
    options: [
      { code: "pre_refunded", label: "PreRefunded" },
      { code: "escrowed", label: "Escrowed" },
    ],
  },
] as const;

/** Flat option list derived from {@link BOND_ATTRIBUTE_GROUPS} (stable API codes). */
export const BOND_ATTRIBUTE_OPTIONS: readonly BondAttributeOption[] =
  BOND_ATTRIBUTE_GROUPS.flatMap((g) => g.options);

const CODE_SET = new Set<string>(BOND_ATTRIBUTE_OPTIONS.map((o) => o.code));

export function isBondAttributeCode(value: string): value is BondAttributeCode {
  return CODE_SET.has(value);
}

const LABEL_BY_CODE = Object.fromEntries(
  BOND_ATTRIBUTE_OPTIONS.map((o) => [o.code, o.label]),
) as Record<BondAttributeCode, string>;

export function bondAttributeLabel(code: BondAttributeCode): string {
  return LABEL_BY_CODE[code];
}
